/**
 * Metrics-panel settings — per-user ordering + visibility of the cards
 * in the Accounts › Metrics tab.
 *
 * Every card has a stable `MetricCardId`. The store owns two pieces:
 *   • `order` — the display order. Cards missing from this list (new
 *     versions, cards added after the user last touched their settings)
 *     are appended to the end so they surface automatically without
 *     wiping the user's customisations.
 *   • `hidden` — a set of ids the user has chosen to suppress. A card
 *     can be hidden via the hover-(x) shortcut on desktop or the dialog
 *     toggle; un-hide via the dialog or clicking the (+) tile at the
 *     end of the grid.
 *
 * A card's "is displayed" state = `!hidden.has(id)`. That derivation
 * lives at the call site so the store stays shape-simple.
 *
 * Persisted to localStorage under `whb-metrics-settings` (keyed under
 * `version` so future schema changes drop stale state cleanly).
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Stable identifier for each Metrics-panel card. Add new ids at the
 * bottom of the union AND the bottom of `DEFAULT_ORDER`, never in the
 * middle — the stable id is what the persisted user order keys against.
 *
 * Keep in sync with the `METRIC_CARD_CATALOG` map in AccountsPageV2.
 */
export type MetricCardId =
  | "weeklyTwr"
  | "previousDay"
  | "weightedApy"
  | "ytd"
  | "pastMonth"
  | "allTimeReturn"
  | "cash"
  | "netDeposits"
  | "stocks"
  | "holdings"
  | "bots"
  | "maxDrawdown"
  | "sharpe"
  | "maxRecovery";

/**
 * Default display order. Also the canonical "every card" list —
 * anything missing from a persisted user order is appended from here
 * so freshly-shipped cards appear automatically.
 */
export const DEFAULT_ORDER: readonly MetricCardId[] = [
  "weeklyTwr",
  "previousDay",
  "weightedApy",
  "ytd",
  "pastMonth",
  "allTimeReturn",
  "cash",
  "netDeposits",
  "stocks",
  "holdings",
  "bots",
  "maxDrawdown",
  "sharpe",
  "maxRecovery"
];

interface MetricsSettingsState {
  order: MetricCardId[];
  hidden: MetricCardId[];

  /** When `true` and the chart is on a non-ALL range, the metrics
   *  panel renders an extra "Range" section above the per-card grid
   *  with Range Return / Weighted APY / Max DD / Sharpe / YTD scoped
   *  to the active window. Default `true` per Sergey 2026-04-30
   *  ("Enabled by default"). Toggle in MetricsSettingsDialog. */
  showRangeMetrics: boolean;

  /** Setter for the range-metrics toggle. */
  setShowRangeMetrics: (v: boolean) => void;

  /** Move `id` to index `toIndex` within the user order. No-op if
   *  `id` isn't present; silently clamps `toIndex` in range. */
  reorder: (id: MetricCardId, toIndex: number) => void;

  /** Mark `id` as hidden. Position in `order` is preserved so un-hide
   *  restores the card to its earlier slot. */
  hide: (id: MetricCardId) => void;

  /** Clear the hidden flag for `id`. */
  show: (id: MetricCardId) => void;

  /** Toggle the hidden flag. */
  toggleHidden: (id: MetricCardId) => void;

  /** Reset to factory defaults — full catalog, no hidden cards. */
  reset: () => void;
}

const defaultOrder = (): MetricCardId[] => DEFAULT_ORDER.slice();

export const useMetricsSettingsStore = create<MetricsSettingsState>()(
  persist(
    (set) => ({
      order: defaultOrder(),
      hidden: [],
      showRangeMetrics: true,
      setShowRangeMetrics: (v) => set({ showRangeMetrics: v }),
      reorder: (id, toIndex) =>
        set((s) => {
          const from = s.order.indexOf(id);
          if (from < 0) return s;
          const next = s.order.slice();
          next.splice(from, 1);
          const clamped = Math.max(0, Math.min(toIndex, next.length));
          next.splice(clamped, 0, id);
          return { order: next };
        }),
      hide: (id) =>
        set((s) =>
          s.hidden.includes(id) ? s : { hidden: [...s.hidden, id] }
        ),
      show: (id) =>
        set((s) => ({ hidden: s.hidden.filter((x) => x !== id) })),
      toggleHidden: (id) =>
        set((s) =>
          s.hidden.includes(id)
            ? { hidden: s.hidden.filter((x) => x !== id) }
            : { hidden: [...s.hidden, id] }
        ),
      reset: () =>
        set({ order: defaultOrder(), hidden: [], showRangeMetrics: true })
    }),
    {
      name: "whb-metrics-settings",
      storage: createJSONStorage(() => localStorage),
      version: 1,
      // Merge any new card ids from DEFAULT_ORDER into the persisted
      // state so shipping a new card appends it instead of hiding it
      // behind "not in user's order list → invisible" logic.
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted as object) } as MetricsSettingsState;
        const known = new Set(merged.order);
        DEFAULT_ORDER.forEach((id) => {
          if (!known.has(id)) merged.order.push(id);
        });
        // Drop ids from `order` that no longer exist (catalog removals).
        merged.order = merged.order.filter((id) =>
          DEFAULT_ORDER.includes(id)
        );
        merged.hidden = merged.hidden.filter((id) =>
          DEFAULT_ORDER.includes(id)
        );
        return merged;
      }
    }
  )
);
